import type { ResultListState } from '../state/state.types';
import type {
  FilterItem,
  LatLonPair,
  SearchField,
} from '../models/search-result';
import type { VoyagerSearchContextState } from './searchContext.types';

export const normalizeFilterSectionId = (sectionId?: string): string => {
  if (!sectionId) {
    return '';
  }

  const trimmedSectionId = sectionId.trim();

  if (trimmedSectionId.startsWith('value-category:')) {
    const [, actualSectionId] = trimmedSectionId.split(':');
    return actualSectionId || trimmedSectionId;
  }

  const firstColonIndex = trimmedSectionId.indexOf(':');
  if (firstColonIndex > 0) {
    return trimmedSectionId.substring(0, firstColonIndex);
  }

  return trimmedSectionId;
};

export const initialSearchState: ResultListState = {
  id: 'voyager-search',
  isLoading: false,
  query: {
    filters: [],
    spatialFilter: {
      bbox: [],
      isChecked: false,
    },
    pageIndex: 0,
    pageSize: 25,
    search: '',
    sortBy: 'date',
    sortOrder: 'desc',
    settings: {
      searchFields: ['any'],
    },
  },
  results: {
    count: 0,
    data: [],
    filterSections: [],
    autocomplete: [],
  },
};

export const getInitialState = (): VoyagerSearchContextState => ({
  searchState: {
    id: 'voyager-search',
    isLoading: true,
    query: {
      filters: [],
      spatialFilter: { bbox: [], isChecked: false },
      pageIndex: 0,
      pageSize: 48,
      search: '',
      sortBy: 'date',
      sortOrder: 'desc' as 'desc' | 'asc',
      settings: { searchFields: ['any'] },
    },
    results: {
      count: 0,
      data: [],
      filterSections: [],
      autocomplete: [],
    },
  },
  isLoading: true,
  activeFilter: 'all',
  facets: [],
  heatmapData: null,
  hoveredItem: null,
  displayConfig: null,
});

export const ensureSearchFields = (fields?: SearchField[]): SearchField[] => {
  if (!fields || fields.length === 0) {
    return ['any'];
  }
  return fields;
};

export const normalizeFilters = (filters: FilterItem[] = []): FilterItem[] =>
  filters.reduce((acc, filter) => {
    if (!filter?.sectionId || !filter?.id) {
      return acc;
    }

    const normalizedSectionId = normalizeFilterSectionId(filter.sectionId);
    if (!normalizedSectionId) {
      return acc;
    }

    const alreadyExists = acc.some(
      (existingFilter) =>
        existingFilter.sectionId === normalizedSectionId &&
        existingFilter.id === filter.id,
    );

    if (!alreadyExists) {
      acc.push({
        sectionId: normalizedSectionId,
        id: filter.id,
        label: filter.label || filter.id,
        isChecked: filter.isChecked ?? true,
        count: filter.count,
      });
    }

    return acc;
  }, [] as FilterItem[]);

export const normalizeBbox = (bbox: Array<LatLonPair | number[]> = []) =>
  bbox.map((pair) => {
    if (Array.isArray(pair)) {
      const [lng, lat] = pair;
      return { longitude: lng, latitude: lat };
    }
    return {
      longitude: pair?.longitude,
      latitude: pair?.latitude,
    };
  });
